// ============================================================================
// CANCUN100 — contabilidadV3.js  (Contabilidad v3 · Bloques 42-49: helpers compartidos, sin UI)
// ----------------------------------------------------------------------------
// Reglas del Plan Maestro §1 que viven aquí:
//  · Ítems de servicios_detalle con item_id estable, tipo_item, fecha_actividad, es_prepago, fecha_limite_pago,
//    cuenta_proveedor, estado_reserva y reemplaza_a (Bloque 42). Los campos viejos no cambian.
//  · Prepagos del folio = Σ neto de ítems es_prepago (Sala/admin; Top Tours = 0). Depósito requerido = prepagos + margen.
//    Liberación: base = max(0, pagos − prepagos); cobrable = comisión × min(base, margen) / margen (Bloque 43).
//  · Periodo = 'YYYY-MM' del mes en que ocurre el movimiento (Bloque 49).
// ============================================================================

// Bloque 53: Balance real. El día cero de la contabilidad v3 y el saldo de caja con el que arrancó (lo define Marco; null = por definir).
export const DIA_CERO = '2026-10';
export const SALDO_INICIAL = 0; // Bloque 54 (Marco, 25-oct): octubre arranca en ceros; al cierre se concilia con el banco en un bloque aparte

export const TIPOS_ITEM = {
  tour: 'Tour', combo: 'Combo Xcaret', hotel: 'Hotel', vuelo: 'Vuelo', transporte: 'Transporte', personalizado: 'Personalizado',
};
// Orden del selector en "Servicios adicionales": personalizado al final (regla del bloque).
// APP 2/05 (F2): Transporte · Hotel · Vuelo · Tour privado · Personalizado. Top Tours: Transporte · Tour privado · Personalizado.
export const TIPOS_ADICIONALES = [['transporte', 'Transporte'], ['hotel', 'Hotel'], ['vuelo', 'Vuelo'], ['tour_privado', 'Tour privado'], ['general', 'Personalizado']];
export const TIPOS_ADICIONALES_TT = [['transporte', 'Transporte'], ['tour_privado', 'Tour privado'], ['general', 'Personalizado']]; // Top Tours: sin hotel ni vuelo ni prepago

export const CUENTAS_PROVEEDOR = [
  ['xperiencias_caribe', 'Xperiencias Caribe'], ['live_caribe', 'Live Caribe Now'], ['transportes', 'Transportes'], ['vuelos', 'Vuelos (prepago)'], ['hoteles', 'Hoteles'],
];
export const nombreCuenta = (clave) => (CUENTAS_PROVEEDOR.find(([k]) => k === clave) || [clave, clave || '—'])[1];

export const ESTADOS_RESERVA = {
  // APP 2/20 Fase B: tokens del tema (claro/oscuro); quien los mezcle con alfa usa color-mix, no hex+alfa.
  pendiente: { label: 'Pendiente', color: 'var(--au-text-faint)' }, enviado: { label: 'Enviado', color: 'var(--au-blue-fg)' }, confirmado: { label: 'Confirmado', color: 'var(--au-success-fg)' },
  cancelado: { label: 'Cancelado', color: 'var(--au-error-fg)' }, reemplazado: { label: 'Reemplazado', color: 'var(--au-yellow-fg)' }, revisar: { label: 'Revisar', color: 'var(--au-error-fg)' }, // B51: lo deja la función cuando la puerta no respondió
};

// item_id corto y estable (8 caracteres base36). Nunca cambia una vez asignado.
export const nuevoItemId = () => {
  try { if (typeof crypto !== 'undefined' && crypto.randomUUID) return crypto.randomUUID().replace(/-/g, '').slice(0, 12); } catch (e) { /* sin crypto */ }
  return (Date.now().toString(36) + Math.random().toString(36).slice(2, 8)).slice(-12);
};

// tipo_custom del formulario → tipo_item del ítem.
// APP 2/05: el tour privado de la Biblia (TEC) cuenta como Transporte.
export const tipoItemDeCustom = (tipoCustom) => ({ general: 'personalizado', hotel: 'hotel', vuelo: 'vuelo', transporte: 'transporte', tour_privado: 'transporte' }[tipoCustom || 'general'] || 'personalizado');
// tipo_item → tipo_custom del formulario (para reconstruir).
export const tipoCustomDeItem = (tipoItem) => ({ personalizado: 'general', hotel: 'hotel', vuelo: 'vuelo', transporte: 'transporte' }[tipoItem] || 'general');

// Cuenta de proveedor por default según tipo y proveedor de la biblia.
export const cuentaDefault = (tipoItem, provObj) => {
  if (tipoItem === 'vuelo') return 'vuelos';
  if (tipoItem === 'hotel') return 'hoteles';
  if (tipoItem === 'transporte') return 'transportes';
  return (provObj && provObj.cuenta_proveedor) || 'xperiencias_caribe';
};
// Prepago por default: vuelo siempre; biblia según proveedores.prepago_default (CDJ, NIK); lo demás no.
export const prepagoDefault = (tipoItem, provObj) => (tipoItem === 'vuelo' ? true : !!(provObj && provObj.prepago_default));

// Lee servicios_detalle (jsonb o string) como arreglo.
export const itemsDe = (reserva) => {
  let d = reserva && reserva.servicios_detalle;
  if (typeof d === 'string') { try { d = JSON.parse(d); } catch (e) { d = []; } }
  return Array.isArray(d) ? d : [];
};
export const itemVivo = (it) => !it || !['cancelado', 'reemplazado'].includes(it.estado_reserva);
export const num = (v) => { const x = Number(v); return Number.isFinite(x) ? x : 0; };
// APP 2/23: tolerancia por folio para dar un depósito o balance por completo (antes $1). La vista cupones_admin exige exactitud: Claude la ajusta.
export const TOLERANCIA_MXN = 10;

// Σ neto de ítems prepago vivos (Top Tours = 0 por regla).
export const prepagosDe = (items, esTopTours = false) => (esTopTours ? 0 : (items || []).filter((it) => itemVivo(it) && it.es_prepago).reduce((s, it) => s + num(it.subtotal_neto), 0));
export const itemsPrepago = (items) => (items || []).filter((it) => itemVivo(it) && it.es_prepago);

// Bloque 43: base de liberación = max(0, pagos − prepagos); cobrable = comisión × min(base, margen) / margen.
// Sin prepagos (o TT/admin sin prepagos) devuelve el cobrable de la vista tal cual.
export const cobrableConPrepagos = ({ comision, margen, pagos, prepagos, cobrableVista }) => {
  const pre = num(prepagos);
  if (pre <= 0) return cobrableVista != null ? num(cobrableVista) : num(comision);
  const m = num(margen); const c = num(comision);
  if (m <= 0 || c <= 0) return 0;
  const base = Math.max(0, num(pagos) - pre);
  return Math.round(c * Math.min(base, m) / m * 100) / 100;
};
// Depósito requerido para liberar la comisión completa.
export const depositoRequerido = ({ prepagos, margen }) => Math.max(0, num(prepagos) + num(margen));

// Bloque 42: qué le falta al cupón para estar completo (lista vacía = completo).
export const faltantesCupon = ({ items, deposito, metodo, cliente, adultos, esTopTours = false }) => {
  const f = [];
  const vivos = (items || []).filter(itemVivo);
  if (!String(cliente || '').trim()) f.push('Nombre del cliente');
  if (!(num(adultos) > 0)) f.push('Cantidad de personas');
  if (!vivos.length) f.push('Al menos un servicio');
  vivos.forEach((it, i) => {
    const nom = it.nombre || `ítem ${i + 1}`;
    if (!it.tipo_item) f.push(`Tipo del servicio «${nom}»`);
    if (!it.fecha_actividad && !it.fecha) f.push(`Fecha de «${nom}»`);
    if (!(num(it.subtotal_neto) > 0) && !it.es_combo) f.push(`Neto de «${nom}»`);
    if (!(num(it.subtotal_publico) > 0) && !it.es_combo) f.push(`Precio de «${nom}»`);
    if (!esTopTours && it.tipo_item === 'hotel' && !it.es_prepago && !it.fecha_limite_pago) f.push(`Fecha límite de pago del hotel «${nom}»`);
    // APP 2/05 (F2): obligatorios propios de cada servicio personalizado (los calcula faltantesCustom en el formulario).
    (Array.isArray(it.faltantes) ? it.faltantes : []).forEach((x) => { const t = `${x} de «${nom}»`; if (!f.includes(t)) f.push(t); });
  });
  if (!(num(deposito) > 0)) f.push('Depósito declarado'); // APP 2/15: el depósito se teclea otra vez (corrige 2/11)
  if (!String(metodo || '').trim()) f.push('Método de pago');
  return f;
};

// 'YYYY-MM' del mes en que ocurre un movimiento (hoy por default).
export const periodoDe = (fecha) => { const d = fecha ? new Date(fecha) : new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`; };
export const periodoAnterior = (periodo) => { const [y, m] = String(periodo || periodoDe()).split('-').map(Number); const d = new Date(y, m - 2, 1); return periodoDe(d); };
export const etiquetaPeriodo = (p) => { const M = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic']; const [y, m] = String(p || '').split('-'); return M[Number(m) - 1] ? `${M[Number(m) - 1]} ${y}` : (p || '—'); };

// Texto de "Pago inicial" compartido por los 3 paneles (Bloque 43): mismas palabras en Mis ventas, Pagar Comisiones y Control Folios.
export const resumenPagoInicial = ({ depositoInicial, fechaDeposito, prepagos, margen, pagos }) => {
  const req = depositoRequerido({ prepagos, margen });
  const cubierto = num(pagos) + TOLERANCIA_MXN >= req; // APP 2/23: tolerancia ±$10 (antes $1, Plan §6)
  const falta = Math.max(0, req - num(pagos));
  return { requerido: req, cubierto, falta, depositoInicial: num(depositoInicial), fechaDeposito: fechaDeposito || null, prepagos: num(prepagos) };
};

// ── Bloque 45: cómo se cobró un balance y recibo de caja ──
export const TIPOS_PAGO = [
  ['balance', 'Transferencia / terminal a la agencia'],
  ['balance_llegada', 'Cobrado a la llegada por el agente (efectivo · recibo de caja)'],
  ['balance_transporte', 'Cobrado en transporte (efectivo · recibo de caja)'],
  ['balance_cobrado_xc', 'Cobrado por Xperiencias Caribe en el tour (no entra a nuestra caja)'],
];
export const etiquetaTipoPago = (t) => (TIPOS_PAGO.find(([k]) => k === t) || [t, t === 'deposito_inicial' ? 'Depósito inicial' : (t || 'Balance')])[1];
export const requiereRecibo = (t) => t === 'balance_llegada' || t === 'balance_transporte';
export const formatearRecibo = (n) => `RC-${String(Math.max(1, Number(n) || 1)).padStart(4, '0')}`;
// Siguiente folio RC-<n>: max(recibo_caja) + 1 leyendo pagos_cliente (sin secuencia en la base ni en localStorage).
export const siguienteReciboCaja = async (supabase) => {
  try {
    const { data } = await supabase.from('pagos_cliente').select('recibo_caja').not('recibo_caja', 'is', null).order('recibo_caja', { ascending: false }).limit(50);
    const max = (data || []).reduce((m, r) => { const k = parseInt(String(r.recibo_caja || '').replace(/\D/g, ''), 10); return Number.isFinite(k) && k > m ? k : m; }, 0);
    return formatearRecibo(max + 1);
  } catch (e) { return formatearRecibo(Date.now() % 100000); }
};

// ── Bloque 51: envío a Xperiencias Caribe por la puerta de Komito (Edge Function enviar-reserva-xc) ──
// Solo estos agentes (sesión, sandbox o switch) ven el botón mientras dura la prueba; el resto sigue con WhatsApp (B46).
export const XC_PRUEBA_AGENTES = [35]; // Marco Fantasma
export const puedeEnviarXC = (agenteId) => agenteId != null && XC_PRUEBA_AGENTES.includes(Number(agenteId));
// Ítem que opera XC: cuenta xperiencias_caribe y tipo tour / combo / personalizado (hotel, vuelo y transporte no van).
export const esItemXC = (it) => !!it && (it.cuenta_proveedor || 'xperiencias_caribe') === 'xperiencias_caribe' && !['hotel', 'vuelo', 'transporte'].includes(String(it.tipo_item || 'tour'));
const DIAS_XC = ['domingo', 'lunes', 'martes', 'miércoles', 'jueves', 'viernes', 'sábado'];
const MESES_XC = ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'];
const fechaLargaXC = (iso) => { const m = String(iso || '').match(/^([0-9]{4})-([0-9]{2})-([0-9]{2})/); if (!m) return 'SIN FECHA'; const d = new Date(Date.UTC(+m[1], +m[2] - 1, +m[3])); return `${DIAS_XC[d.getUTCDay()]} ${m[3]}-${MESES_XC[+m[2] - 1]}-${m[1]}`; };
const fmtXC = (v) => '$' + Number(v || 0).toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 });
export const folioCortoXC = (s) => String(s || '').replace(/[^A-Za-z0-9._/-]/g, '').slice(0, 20);
// Mismo texto que arma la función (plantilla §6): solo para la vista previa; lo que se manda lo arma la función.
export const textoXC = ({ reserva, item, n = 1, total = 1 }) => {
  const r = reserva || {}; const it = item || {};
  const ad = Number(it.composicion_editada ? it.adultos : (r.adultos ?? it.adultos)) || 0;
  const men = Number(it.composicion_editada ? it.menores : (r.menores ?? it.menores)) || 0;
  const inf = Number(r.infantes ?? it.infantes) || 0;
  const balance = Math.max(0, Number(r.total || 0) - Number(r.deposito || 0));
  const cobrar = (balance > 1 && ['B1', 'BP'].includes(String(r.pago_balance || '').toUpperCase())) ? `MXN ${fmtXC(balance)} (balance del paquete, lo cobra XC en el tour)` : 'PAGADO';
  const folioCupon = String(r.folio_personalizado || r.folio || r.id || '');
  return {
    folio: folioCortoXC(`${folioCupon}-${n}`),
    texto: [
      `*RESERVA ${n}/${total}* · Cupón ${folioCupon}`,
      `👤 Titular: ${String(r.nombre || '').trim() || '—'}`,
      `🏨 Hotel: ${String(r.hotel || '').trim() || '—'} · Hab. ${String(r.habitacion || '').trim() || 'pendiente'}`,
      `👥 Pax: ${ad} ad · ${men} men · ${inf} inf (${ad}.${men}.${inf})`,
      `🎟️ Servicio: ${it.nombre || it.sku} (${it.sku || '—'})`,
      `📅 Fecha: ${fechaLargaXC(it.fecha_actividad || it.fecha)} · Horario: ${it.turno || 'pick up lo asigna XC'}`,
      `💵 Cobrar en mano: ${cobrar}`,
      `📝 Notas: ${String(it.notas || r.notas || '').trim() || '—'}`,
      '↩️ Respondan CITANDO este mensaje con su bloque «Tu confirmación», o con ❌ y el motivo.',
    ].join('\n').slice(0, 1000),
  };
};
// Traduce la respuesta de la función / puerta (§3) a qué hacer en la app.
export const interpretarErrorXC = (det) => {
  const err = String(det?.error || '');
  if (err.startsWith('timeout_o_red')) return { tipo: 'revisar', texto: 'Revisar: la llamada se cortó sin respuesta. Ver el grupo antes de reenviar.' };
  if (err === 'limite') return { tipo: 'limite', espera: Number(det?.reintentar_en_s) || 60, texto: `Límite de la puerta: espera ${Number(det?.reintentar_en_s) || 60} s (se reintenta solo una vez).` };
  if (err === 'usuario_no_autorizado') return { tipo: 'fatal', texto: 'Tu usuario no está autorizado en Komito.' };
  if (err === 'SIN_LLAVE') return { tipo: 'fatal', texto: 'Falta configurar la llave (Marco).' };
  if (err === 'YA_ENVIADO') return { tipo: 'fatal', texto: det?.mensaje || 'Este producto ya se mandó; no se reenvía para no duplicar.' };
  if (err === 'NO_XC') return { tipo: 'fatal', texto: 'Este producto no lo opera Xperiencias Caribe.' };
  if (err === 'SESION' || err === 'SIN_AGENTE') return { tipo: 'fatal', texto: det?.mensaje || 'Inicia sesión con una cuenta ligada a un agente.' };
  if (err === 'fallo_envio' || err === 'apagado' || err === 'sin_conversacion' || /^http_5[0-9][0-9]$/.test(err) || err === 'PUERTA') return { tipo: 'reintentar', texto: `La puerta no aceptó el mensaje (${err}). Puedes reintentar.` };
  return { tipo: 'reintentar', texto: det?.mensaje || `Error ${err || 'desconocido'}.` };
};
